import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { getUtcDate } from '@common-fe/common-fe';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { API_URL, IEmployerTerminationPropertiesDto } from '@/dtos';

const QUERY_KEY = 'useGetEmployerTerminationDataQueryKey';

const employerTerminationDataFormatter = (
  dto: IEmployerTerminationPropertiesDto
): EmployerTerminationData => ({
  id: dto.id || 0,
  organizationId: dto.organization_id || 0,
  terminationDate: getUtcDate(dto.termination_date).format(DEFAULT_DATE_FORMAT),
  coverageEndDate: dto.coverage_end_date
    ? getUtcDate(dto.coverage_end_date).format(DEFAULT_DATE_FORMAT)
    : null,
  claimLastDate: dto.claim_last_date ? getUtcDate(dto.claim_last_date).format(DEFAULT_DATE_FORMAT) : null,
  bulkTransfer: dto.bulk_transfer || false,
});

export interface EmployerTerminationData {
  id: number;
  organizationId: number;
  terminationDate: string;
  coverageEndDate?: string | null;
  claimLastDate?: string | null;
  bulkTransfer: boolean;
}

export const useGetEmployerTerminationDataQuery = (orgId?: string) => {
  const { isLoading, isSuccess, data, refetch, isError } = useQuery(
    [orgId, QUERY_KEY],
    orgId ? () => api.get(API_URL.ORGANIZATIONS_TERMINATION_BY_ORGANIZATION_ID(orgId)) : () => null,
    {
      retry: false,
      cacheTime: 0,
    }
  );

  const terminationData = useMemo(() => {
    if (isError) {
      return undefined;
    }
    return data?.data && employerTerminationDataFormatter(data.data);
  }, [data, isError]);

  return {
    data: data?.data,
    terminationData,
    isLoading,
    isSuccess,
    refetch,
  };
};
